import React, { useCallback, ReactNode } from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import Checkbox from '@adsk/alloy-react-checkbox';
import { CheckmarkIcon } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

export type MenuOptionProps = StylableComponent<HTMLLIElement> & {
  isSelected?: boolean;
  isHighlighted?: boolean;
  isMultiple?: boolean;
  disabled?: boolean;
  text?: ReactNode;
  subtext?: ReactNode;
  renderLeftComponent?: () => ReactNode;
  renderRightComponent?: () => ReactNode;
};

const Label = styledC.div<{
  $isSelected?: boolean;
  $disabled?: boolean;
}>`
  ${theme.typography.ellipsis}
  ${(props) =>
    props.$isSelected && !props.$disabled
      ? theme.typography.bodyMediumBold
      : theme.typography.bodyMedium}
  color: ${(props) =>
    props.$disabled ? theme.tokens.colors.text.inactive.value : 'inherit'};
  width: 100%;
`;

const Subtext = styledC.div<{ disabled?: boolean }>`
  ${theme.typography.ellipsis}
  ${theme.typography.bodyMedium}
color: ${(props) =>
  props.disabled
    ? theme.tokens.colors.text.inactive.value
    : theme.tokens.colors.text.secondary.value};
`;

type StyledLiProps = {
  disabled?: boolean;
  $isSelected?: boolean;
  $isMultiple?: boolean;
  $isHighlighted?: boolean;
};

const StyledLi = styledC.li<StyledLiProps>`
  display: flex;
  align-items: flex-start;
  box-sizing: border-box;
  padding: ${theme.spacing.XS}px ${theme.spacing.M}px;
  cursor: ${(props) => (props.disabled ? undefined : 'pointer')};
  background-color: ${(props) =>
    props.$isSelected && !props.$isMultiple
      ? theme.tokens.colors.background.selected.subtler.value
      : props.$isHighlighted && !props.disabled
        ? theme.tokens.colors.background.neutral.hover.value
        : undefined};
  &:active {
    background-color: ${(props) =>
      props.disabled
        ? undefined
        : theme.tokens.colors.background.neutral.pressed.value};
  }
`;

const MenuItem = React.forwardRef<HTMLLIElement, MenuOptionProps>(
  (
    {
      isSelected,
      isMultiple,
      isHighlighted,
      disabled,
      text: label,
      subtext,
      renderLeftComponent,
      renderRightComponent,
      onClick: rawOnClick,
      style,
      ...props
    },
    ref,
  ) => {
    const onClick: React.MouseEventHandler<HTMLLIElement> = useCallback(
      (args) => {
        if (!disabled) {
          rawOnClick?.(args);
        }
      },
      [disabled, rawOnClick],
    );

    return (
      <StyledLi
        ref={ref}
        disabled={disabled}
        $isSelected={isSelected}
        $isMultiple={isMultiple}
        $isHighlighted={isHighlighted}
        css={style}
        onClick={onClick}
        {...props}
      >
        {isMultiple && (
          <Checkbox
            checked={isSelected}
            style={{
              marginRight: theme.spacing.XS,
              marginTop: subtext ? theme.spacing.XXXS : theme.spacing.XXS,
            }}
            disabled={disabled}
            readOnly
          />
        )}
        {renderLeftComponent && renderLeftComponent()}
        <div
          css={{
            flex: 1,
            overflow: 'hidden',
            ...(!subtext && {
              display: 'flex',
              alignItems: 'center',
              minHeight: 24,
            }),
          }}
        >
          {label && (
            <Label $disabled={disabled} $isSelected={isSelected}>
              {label}
            </Label>
          )}
          {subtext && <Subtext disabled={disabled}>{subtext}</Subtext>}
        </div>
        {renderRightComponent
          ? renderRightComponent()
          : !disabled &&
            isSelected &&
            !isMultiple && (
              <CheckmarkIcon
                style={{
                  color: theme.tokens.colors.icon.selected.value,
                  padding: `-${theme.spacing.XXXS}px, 0px`,
                }}
              />
            )}
      </StyledLi>
    );
  },
);

MenuItem.displayName = 'MenuOption';

MenuItem.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Whether menu option is currently selected */
  isSelected: PropTypes.bool,
  /** Whether menu option is currently highlighted.  Used when cursor navigation */
  isHighlighted: PropTypes.bool,
  /** Whether menu option is in a multi select */
  isMultiple: PropTypes.bool,
  /** Whether menu option is disabled */
  disabled: PropTypes.bool,
  /** Primary content */
  text: PropTypes.any,
  /** Secondary content */
  subtext: PropTypes.any,
  /** component to render to the left of the item */
  renderLeftComponent: PropTypes.func,
  /** component to render to the right of the item */
  renderRightComponent: PropTypes.func,
  /** Function executed on click */
  onClick: PropTypes.func,
};

export default MenuItem;
